import React, { useState, useMemo, useRef, useEffect } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface SearchableItem {
  name: string;
  code: string;
  aliases: string[];
}

export interface SearchableComboboxProps {
  items: SearchableItem[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  "aria-label"?: string;
  "aria-describedby"?: string;
}

export function SearchableCombobox({
  items,
  value,
  onValueChange,
  placeholder = "Select item...",
  searchPlaceholder = "Search...",
  emptyMessage = "No items found.",
  className,
  disabled = false,
  icon,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
}: SearchableComboboxProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const buttonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus management for accessibility
  useEffect(() => {
    if (open && inputRef.current) {
      // Focus search input when popover opens
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Search function that matches name and aliases with improved relevance scoring
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    
    const normalizedQuery = searchQuery.toLowerCase().trim();
    
    // Filter and score results for better relevance
    const scoredItems = items
      .filter(item => 
        item.name.toLowerCase().includes(normalizedQuery) ||
        item.aliases.some(alias => alias.toLowerCase().includes(normalizedQuery))
      )
      .map(item => {
        let score = 0;
        const normalizedName = item.name.toLowerCase();
        
        // Exact name match gets highest score
        if (normalizedName === normalizedQuery) score = 1000;
        // Name starts with query gets high score
        else if (normalizedName.startsWith(normalizedQuery)) score = 500;
        // Name contains query gets medium score
        else if (normalizedName.includes(normalizedQuery)) score = 100;
        // Alias matches get lower scores
        else {
          const aliasMatch = item.aliases.find(alias => 
            alias.toLowerCase().includes(normalizedQuery)
          );
          if (aliasMatch) {
            if (aliasMatch.toLowerCase() === normalizedQuery) score = 300;
            else if (aliasMatch.toLowerCase().startsWith(normalizedQuery)) score = 150;
            else score = 50;
          }
        }
        
        return { item, score };
      })
      .sort((a, b) => b.score - a.score || a.item.name.localeCompare(b.item.name))
      .map(({ item }) => item);

    return scoredItems;
  }, [items, searchQuery]);

  // Find selected item for display
  const selectedItem = items.find(item => item.name === value);

  // Enhanced keyboard handling
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      setOpen(false);
      buttonRef.current?.focus();
    }
  };

  const handleSelect = (selectedValue: string) => {
    onValueChange(selectedValue);
    setOpen(false);
    setSearchQuery("");
    // Return focus to trigger button for better UX
    setTimeout(() => {
      buttonRef.current?.focus();
    }, 100);
  };

  // Generate accessible label
  const accessibleLabel = ariaLabel || `Select ${placeholder.toLowerCase().replace(/\W/g, '')}`;
  const resultsCount = filteredItems.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={buttonRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={accessibleLabel}
          aria-describedby={ariaDescribedBy}
          aria-haspopup="listbox"
          className={cn(
            // Match original Select styling exactly
            "h-12 w-full justify-between border-input bg-card text-left font-normal",
            "hover:bg-accent hover:text-accent-foreground",
            "focus:ring-2 focus:ring-ring focus:ring-offset-2",
            "data-[state=open]:bg-accent data-[state=open]:text-accent-foreground",
            // Ensure text doesn't get cut off
            "text-sm leading-tight",
            disabled && "cursor-not-allowed opacity-50",
            className
          )}
          disabled={disabled}
        >
          <div className="flex items-center gap-2 w-full min-w-0">
            {icon && (
              <div className="h-4 w-4 opacity-50 flex-shrink-0" aria-hidden="true">
                {icon}
              </div>
            )}
            <div className="flex-grow text-left truncate">
              <span className={cn(
                selectedItem ? "text-foreground" : "text-muted-foreground"
              )}>
                {selectedItem ? selectedItem.name : placeholder}
              </span>
            </div>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[--radix-popover-trigger-width] p-0" 
        align="start"
        sideOffset={4}
        onKeyDown={handleKeyDown}
      >
        <Command shouldFilter={false}>
          <CommandInput
            ref={inputRef}
            placeholder={searchPlaceholder}
            value={searchQuery}
            onValueChange={setSearchQuery}
            className="h-9 border-0 focus:ring-0"
            aria-label="Search in options"
            aria-describedby={resultsCount > 0 ? "search-results-count" : undefined}
          />
          {/* Screen reader announcement for search results */}
          {searchQuery && (
            <div 
              id="search-results-count" 
              className="sr-only" 
              aria-live="polite"
              aria-atomic="true"
            >
              {resultsCount === 0 
                ? "No results found" 
                : `${resultsCount} result${resultsCount === 1 ? '' : 's'} available`
              }
            </div>
          )}
          <CommandList 
            className="max-h-[300px] overflow-auto"
            role="listbox"
            aria-label="Available options"
          >
            <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
              {emptyMessage}
            </CommandEmpty>
            <CommandGroup>
              {filteredItems.map((item) => (
                <CommandItem
                  key={item.code}
                  value={item.name}
                  onSelect={() => handleSelect(item.name)}
                  role="option"
                  aria-selected={value === item.name}
                  className={cn(
                    "flex items-center justify-between cursor-pointer",
                    "px-3 py-2 text-sm",
                    "hover:bg-accent hover:text-accent-foreground",
                    "focus:bg-accent focus:text-accent-foreground focus:outline-none",
                    "data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground",
                    // Ensure proper spacing for mobile
                    "min-h-[2.5rem] touch-manipulation",
                    // Better focus indicators
                    "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  )}
                >
                  <span className="truncate flex-1">{item.name}</span>
                  <Check
                    className={cn(
                      "ml-2 h-4 w-4 flex-shrink-0",
                      value === item.name ? "opacity-100" : "opacity-0"
                    )}
                    aria-hidden="true"
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
